import React, { useCallback, useEffect, useRef, useState } from "react";
import { parseTimeInput, TIME_PLACEHOLDER } from "@/lib/timeInput";

/**
 * APPOINTMENT-TIME-ENTRY · Plain-text time input used by every
 * shared web appointment create/edit surface.
 *
 * * `type="text"` (NEVER `type="time"` / `datetime-local`).
 * * `inputMode="numeric"` + `maxLength={5}` for a numeric mobile
 *   keypad without hiding the field behind a picker.
 * * Preserves the user's raw typed value while they are editing.
 *   Normalisation to canonical `HH:MM` happens ONLY on blur and
 *   before submit.
 * * `onChange(canonical, errorOrNull)` fires ONLY when the field
 *   commits (blur / hydration). Parent state stays clean and never
 *   sees half-typed values like `"9"`.
 * * Emits a friendly inline error copy — no raw regex or JS errors.
 *
 * Props:
 *   value        — canonical `HH:MM` or "" from the parent.
 *   onChange     — (canonical, errorMessage|null) => void, fired on
 *                  blur (and once on hydration if the incoming
 *                  `value` is set).
 *   error        — parent-controlled inline error copy. Suppressed
 *                  while the user is actively editing.
 *   ariaLabel    — accessible name.
 *   testid       — `data-testid` for the underlying input.
 *   disabled     — passthrough.
 */
export default function TimeTextField({
    value,
    onChange,
    error,
    ariaLabel,
    testid,
    disabled = false,
    className = "",
}) {
    const [draft, setDraft] = useState(value || "");
    const [editing, setEditing] = useState(false);
    const lastCommittedRef = useRef(value || "");

    // External hydration → drop the draft to match. Never wipes an
    // in-progress edit (the effect only runs when the CANONICAL
    // value changes and the field is not currently focused).
    useEffect(() => {
        if (editing) return;
        setDraft(value || "");
        lastCommittedRef.current = value || "";
    }, [value, editing]);

    const commit = useCallback(() => {
        const raw = (draft || "").trim();
        if (!raw) {
            // Empty is not an error per se — the parent decides
            // whether an empty field blocks submission.
            setDraft("");
            if (lastCommittedRef.current !== "") {
                lastCommittedRef.current = "";
                onChange?.("", null);
            }
            return;
        }
        const parsed = parseTimeInput(raw);
        if (parsed) {
            setDraft(parsed);
            if (lastCommittedRef.current !== parsed) {
                lastCommittedRef.current = parsed;
                onChange?.(parsed, null);
            }
        } else {
            // Keep the raw draft visible so the user can fix it.
            lastCommittedRef.current = "";
            onChange?.("", "Enter a valid time (HH:MM, 00:00–23:59).");
        }
    }, [draft, onChange]);

    return (
        <input
            type="text"
            inputMode="numeric"
            maxLength={5}
            placeholder={TIME_PLACEHOLDER}
            value={draft}
            onChange={(e) => {
                setEditing(true);
                setDraft(e.target.value);
            }}
            onFocus={() => setEditing(true)}
            onBlur={() => {
                setEditing(false);
                commit();
            }}
            aria-label={ariaLabel}
            aria-invalid={error ? "true" : undefined}
            data-testid={testid}
            data-time-field="true"
            disabled={disabled}
            className={className}
            autoComplete="off"
        />
    );
}
